






























































import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';

@Component({
  name: 'AudioPlayer',
})
export default class AudioPlayer extends Vue {
  // Props
  @Prop({ type: String, required: false })
  readonly src!: string;

  @Prop({ type: String, required: false })
  readonly downloadUrl!: string;

  @Prop({ type: String, required: false, default: 'Audio' })
  readonly fileName!: string;

  @Prop({ type: Boolean, default: true })
  readonly autoStop!: boolean;

  // Data
  private isPlaying = false;

  private currentTime = 0;

  private duration = 0;

  private progress = 0;

  private hasError = false;

  private isLoading = false;

  private loadAttempts = 0;

  private maxLoadAttempts = 5;

  private loadTimeout: number | null = null;

  private metadataLoadTimeout: number | null = null;

  private internalDownloadUrl: string | null = null;

  // Computed
  get hasValidSource(): boolean {
    return !!this.src && !this.hasError;
  }

  get hasValidDuration(): boolean {
    return this.duration > 0 && Number.isFinite(this.duration) && this.duration !== Infinity;
  }

  get audioElement(): HTMLAudioElement {
    return this.$refs.audioElement as HTMLAudioElement;
  }

  // Watchers
  @Watch('src')
  onSrcChange(newSrc: string, oldSrc: string): void {
    console.log('Source changed:', { oldSrc, newSrc });

    this.resetPlayer();
    this.hasError = false;
    this.loadAttempts = 0;

    this.internalDownloadUrl = newSrc;

    if (newSrc && newSrc !== oldSrc) {
      this.isLoading = true;

      this.$nextTick(() => {
        if (this.audioElement) {
          console.log('Loading new audio source...');
          this.audioElement.load();
          this.startMetadataLoadTimeout();
        }
      });
    }
  }

  // Events
  @Emit('play')
  // eslint-disable-next-line class-methods-use-this
  onPlayEvent(): void {}

  // eslint-disable-next-line class-methods-use-this
  onPauseEvent(): void {}

  @Emit('ended')
  // eslint-disable-next-line class-methods-use-this
  onEndedEvent(): void {}

  @Emit('error')
  // eslint-disable-next-line class-methods-use-this
  onErrorEvent(error: Event): Event {
    return error;
  }

  @Emit('headphones-click')
  // eslint-disable-next-line class-methods-use-this
  onHeadphonesClickEvent(): void {}

  // Methods
  // eslint-disable-next-line class-methods-use-this
  private formatTime(seconds: number): string {
    if (!seconds || Number.isNaN(seconds) || !Number.isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private clearTimeouts(): void {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    if (this.metadataLoadTimeout) {
      clearTimeout(this.metadataLoadTimeout);
      this.metadataLoadTimeout = null;
    }
  }

  private async downloadAudio(): Promise<void> {
    if (!this.downloadUrl) return;

    try {
      const response = await fetch(this.downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = this.fileName || 'audio.mp3';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  }

  private startMetadataLoadTimeout(): void {
    this.clearTimeouts();

    this.metadataLoadTimeout = setTimeout(() => {
      this.isLoading = false;
      this.hasError = true;
    }, 10000);
  }

  private attemptDurationRetrieval(): void {
    if (!this.audioElement || this.hasValidDuration || this.loadAttempts >= this.maxLoadAttempts) {
      return;
    }

    this.loadAttempts += 1;
    if (this.loadAttempts === 1) {
      this.audioElement.load();
    }

    const rawDuration = this.audioElement.duration;
    if (rawDuration && Number.isFinite(rawDuration) && rawDuration !== Infinity) {
      this.duration = rawDuration;
      this.isLoading = false;
      this.clearTimeouts();
      return;
    }

    if (this.loadAttempts < this.maxLoadAttempts) {
      this.loadTimeout = setTimeout(() => {
        this.attemptDurationRetrieval();
      }, 1000 * this.loadAttempts);
    } else {
      this.isLoading = false;
      this.clearTimeouts();
    }
  }

  private resetPlayer(): void {
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.progress = 0;
    this.isLoading = false;
    this.loadAttempts = 0;
    this.clearTimeouts();

    if (this.audioElement) {
      this.audioElement.currentTime = 0;
      this.audioElement.pause();
    }
  }

  public toggleAudio(): void {
    if (!this.hasValidSource || this.isLoading) return;

    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  public play(): void {
    if (!this.hasValidSource || this.isLoading) return;

    if (this.autoStop) {
      this.$emit('request-stop-others', this);
    }

    if (!this.hasValidDuration && this.audioElement) {
      this.attemptDurationRetrieval();
    }

    this.audioElement.play().catch(() => {
      this.hasError = true;
      this.isLoading = false;
    });
  }

  public pause(): void {
    if (this.audioElement) {
      this.audioElement.pause();
    }
  }

  public stop(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
  }

  private seekAudio(event: MouseEvent): void {
    if (!this.hasValidSource || !this.hasValidDuration) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickPercent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = clickPercent * this.duration;

    if (Number.isFinite(newTime)) {
      this.audioElement.currentTime = newTime;
      this.currentTime = newTime;
      this.progress = clickPercent * 100;
    }
  }

  private onLoadStart(): void {
    this.isLoading = true;
    this.hasError = false;
  }

  private onLoadedMetadata(): void {
    const audioDuration = this.audioElement.duration;

    if (audioDuration && Number.isFinite(audioDuration) && audioDuration !== Infinity) {
      this.duration = audioDuration;
      this.isLoading = false;
      this.hasError = false;
      this.clearTimeouts();
    } else {
      this.attemptDurationRetrieval();
    }
  }

  private onTimeUpdate(): void {
    if (!this.audioElement) return;

    const currentTime = this.audioElement.currentTime || 0;
    const { duration } = this.audioElement;

    if (!this.hasValidDuration && duration && Number.isFinite(duration) && duration !== Infinity) {
      this.duration = duration;
      this.isLoading = false;
      this.clearTimeouts();
    }

    this.currentTime = currentTime;

    if (this.hasValidDuration) {
      this.progress = (currentTime / this.duration) * 100;
    }
  }

  private onEnded(): void {
    this.isPlaying = false;
    this.currentTime = 0;
    this.progress = 0;
    this.onEndedEvent();
  }

  private onError(event: Event): void {
    this.hasError = true;
    this.isPlaying = false;
    this.isLoading = false;
    this.clearTimeouts();
    console.error('Erro no áudio:', event);
    this.onErrorEvent(event);
  }

  private onLoadedData(): void {
    if (!this.hasValidDuration && this.audioElement.duration) {
      this.onLoadedMetadata();
    }
  }

  private onDurationChange(): void {
    const { duration } = this.audioElement;

    if (duration && Number.isFinite(duration) && duration !== Infinity) {
      this.duration = duration;
      this.isLoading = false;
      this.clearTimeouts();
    }
  }

  private onCanPlay(): void {
    if (!this.hasValidDuration && this.audioElement.duration) {
      const { duration } = this.audioElement;
      if (Number.isFinite(duration) && duration !== Infinity) {
        this.duration = duration;
        this.isLoading = false;
        this.clearTimeouts();
      }
    }
  }

  private onCanPlayThrough(): void {
    if (!this.hasValidDuration) {
      this.attemptDurationRetrieval();
    }
  }

  private onProgress(): void {
    if (!this.hasValidDuration && this.audioElement.duration) {
      const { duration } = this.audioElement;
      if (Number.isFinite(duration) && duration !== Infinity) {
        this.duration = duration;
        this.isLoading = false;
        this.clearTimeouts();
      }
    }
  }

  private onHeadphonesClick(): void {
    this.onHeadphonesClickEvent();
  }

  mounted(): void {
    console.log('AudioPlayer mounted');
    this.internalDownloadUrl = this.downloadUrl;
    if (this.audioElement) {
      this.audioElement.addEventListener('play', () => {
        this.isPlaying = true;
        this.onPlayEvent();
      });

      this.audioElement.addEventListener('pause', () => {
        this.isPlaying = false;
        this.onPauseEvent();
      });

      if (this.src) {
        this.isLoading = true;
        this.$nextTick(() => {
          this.audioElement.load();
          this.startMetadataLoadTimeout();
        });
      }
    }
  }

  beforeDestroy(): void {
    this.stop();
    this.clearTimeouts();
  }
}
